/**
 * Flow for onboarding teachers via Clever Library.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Spring} from "@khanacademy/wonder-blocks-layout";
import {
    ModalDialog,
    ModalLauncher,
    ModalPanel,
} from "@khanacademy/wonder-blocks-modal";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import type {LoginUserData} from "@khan/auth-shared";
import {mediaQueries} from "@khan/shared-styles";
import {cssUrl, useLocation, type Location} from "@khan/urls";

import StudentConfirmAccountLinkStep from "./clever-library/confirm-account-link-step.tsx";
import CreateAccountStep from "./clever-library/create-account-step.tsx";
import DateOfBirthStep from "./clever-library/date-of-birth-step.tsx";
import ForgotPasswordStep from "./clever-library/forgot-password-step.tsx";
import {logoutCleverUser} from "./clever-library/login-actions.ts";
import LoginStep from "./clever-library/login-step.tsx";
import WelcomeStep from "./clever-library/welcome-step.tsx";
import {MAX_MODAL_WIDTH, WELCOME_IMAGE_HEIGHT} from "./constants.ts";

type Step =
    | "welcome"
    | "create-account"
    | "login"
    | "forgot-password"
    | "date-of-birth"
    | "confirm-account-link"
    | "basic-loader";

interface PanelProps {
    children: React.ReactNode;
    disableBackButton: boolean;
    // Optionally specify how to handle when a "Back" button is clicked.
    // If this prop is not specified, a "Back" button will not be rendered.
    onBack?: () => void;
}

const Panel = ({children, onBack, disableBackButton}: PanelProps) => (
    <ModalPanel
        content={
            <View style={styles.panel}>
                <View style={styles.content}>{children}</View>
                <Spring />
                {onBack && (
                    <View style={styles.backButtonWrapper}>
                        <Button
                            kind="tertiary"
                            onClick={onBack}
                            disabled={disableBackButton}
                        >
                            {t`Back`}
                        </Button>
                    </View>
                )}
            </View>
        }
        closeButtonVisible={false}
    />
);

const getInitialStep = (location: Location): Step => {
    let initialStep: Step = "welcome";
    const step = location.queryParams.step;

    if (step) {
        if (step === "login") {
            initialStep = "login";
        } else if (step === "date-of-birth") {
            initialStep = "date-of-birth";
        }
    }
    return initialStep;
};

/**
 * The ID used by Screen Readers to identify the dialog title.
 */
const dialogTitleId = "clever-library-onboarding-dialog-title";

interface Props {
    cleverId: string;
    isTeacher: boolean;
    cleverEmail?: string;
    transferToken?: string;
}

interface State {
    defaultIdentifier: string;
    step: Step;
    dateOfBirth?: string;
    user: LoginUserData | null | undefined;
    disableBackButton: boolean;
    studentIsDistrictUser: boolean;
}

export default function CleverLibraryOnboarding({
    cleverId,
    isTeacher,
    cleverEmail,
    transferToken,
}: Props) {
    const location = useLocation();
    const [state, setState] = React.useState<State>({
        defaultIdentifier: "",
        step: getInitialStep(location),
        disableBackButton: false,
        studentIsDistrictUser: false,
        user: undefined,
        dateOfBirth: undefined,
    });

    const disableBackButton = () => {
        setState((prev) => ({
            ...prev,
            disableBackButton: true,
        }));
    };

    const enableBackButton = () => {
        setState((prev) => ({
            ...prev,
            disableBackButton: false,
        }));
    };

    const handleBack = () => {
        switch (state.step) {
            case "create-account":
                if (isTeacher) {
                    setState((prev) => ({...prev, step: "welcome"}));
                } else {
                    setState((prev) => ({...prev, step: "date-of-birth"}));
                }
                break;
            case "confirm-account-link":
                setState((prev) => ({
                    ...prev,
                    step: "basic-loader",
                }));
                logoutCleverUser(location, "login");
                break;
            case "login":
            case "date-of-birth":
                setState((prev) => ({...prev, step: "welcome"}));
                break;
            case "forgot-password":
                setState((prev) => ({...prev, step: "login"}));
                break;
            default:
                setState((prev) => ({...prev, step: "welcome"}));
                break;
        }
    };

    const renderStep = () => {
        const {
            step,
            user,
            studentIsDistrictUser,
            defaultIdentifier,
            dateOfBirth,
        } = state;

        switch (step) {
            case "welcome":
                return (
                    <WelcomeStep
                        titleId={dialogTitleId}
                        onChooseCreateAccount={() => {
                            if (isTeacher) {
                                setState((prev) => ({
                                    ...prev,
                                    step: "create-account",
                                }));
                            } else {
                                setState((prev) => ({
                                    ...prev,
                                    step: "date-of-birth",
                                }));
                            }
                        }}
                        onChooseLogin={() => {
                            setState((prev) => ({...prev, step: "login"}));
                        }}
                    />
                );
            case "login":
                return (
                    <LoginStep
                        titleId={dialogTitleId}
                        isTeacher={isTeacher}
                        defaultIdentifier={defaultIdentifier}
                        onForgotPasswordClick={() => {
                            setState((prev) => ({
                                ...prev,
                                step: "forgot-password",
                            }));
                        }}
                        onLoginSubmit={disableBackButton}
                        onLoginError={enableBackButton}
                        transferToken={transferToken}
                        onStudentLogin={(isDistrictUser, user) => {
                            enableBackButton();
                            setState((prev) => ({
                                ...prev,
                                step: "confirm-account-link",
                                user,
                                studentIsDistrictUser: isDistrictUser,
                            }));
                        }}
                    />
                );
            case "confirm-account-link":
                return (
                    <StudentConfirmAccountLinkStep
                        titleId={dialogTitleId}
                        user={user}
                        transferToken={transferToken}
                        isDistrictUser={studentIsDistrictUser}
                        abandonLoginStep="date-of-birth"
                        onAccountLink={disableBackButton}
                        onAbandonLogin={() => {
                            setState((prev) => ({
                                ...prev,
                                step: "basic-loader",
                            }));
                        }}
                    />
                );
            case "create-account":
                return (
                    <CreateAccountStep
                        cleverId={cleverId}
                        cleverEmail={cleverEmail}
                        isTeacher={isTeacher}
                        transferToken={transferToken}
                        dateOfBirth={dateOfBirth}
                        onCreateAccountSubmit={disableBackButton}
                        onCreateAccountError={enableBackButton}
                        onChooseExistingAccount={({defaultIdentifier}) =>
                            setState((prev) => ({
                                ...prev,
                                step: "login",
                                defaultIdentifier,
                            }))
                        }
                        titleId={dialogTitleId}
                    />
                );
            case "date-of-birth":
                return (
                    <DateOfBirthStep
                        onSignUpClick={(dateOfBirth) => {
                            setState((prev) => ({
                                ...prev,
                                step: "create-account",
                                dateOfBirth,
                            }));
                        }}
                        titleId={dialogTitleId}
                    />
                );
            case "basic-loader":
                return (
                    <View>
                        <CircularSpinner size="large" style={styles.spinner} />
                    </View>
                );
            case "forgot-password":
                return <ForgotPasswordStep titleId={dialogTitleId} />;
            default:
                return <View />;
        }
    };

    const showBackButton =
        state.step !== "welcome" && state.step !== "basic-loader";

    return (
        <ModalLauncher
            opened={true}
            onClose={() => {}}
            backdropDismissEnabled={false}
            modal={
                <ModalDialog
                    aria-labelledby={dialogTitleId}
                    style={styles.dialog}
                    above={
                        state.step === "welcome" && (
                            <View style={styles.welcomeAbove} />
                        )
                    }
                >
                    <Panel
                        onBack={showBackButton ? handleBack : undefined}
                        disableBackButton={state.disableBackButton}
                    >
                        {renderStep()}
                    </Panel>
                </ModalDialog>
            }
        />
    );
}

const styles = StyleSheet.create({
    spinner: {
        alignItems: "center",
    },
    backButtonWrapper: {
        paddingBlockStart: spacing.xLarge_32,
        paddingInlineStart: spacing.small_12,
        alignItems: "flex-start",

        [mediaQueries.smOrSmaller]: {
            paddingInlineStart: 0,
        },
    },
    dialog: {
        height: "auto",
        maxWidth: MAX_MODAL_WIDTH,

        [mediaQueries.smOrSmaller]: {
            width: "100%",
            maxWidth: "100%",
            height: "100%",
            maxHeight: "100%",
        },
    },
    content: {
        paddingInlineStart: spacing.xxxLarge_64,
        paddingInlineEnd: spacing.xxxLarge_64,

        [mediaQueries.smOrSmaller]: {
            paddingBlockStart: spacing.xxxLarge_64,
            paddingInlineStart: 0,
            paddingInlineEnd: 0,
        },
    },
    panel: {
        flex: 1,
    },
    welcomeAbove: {
        backgroundImage: cssUrl("/images/clever/welcome-modal-image.png"),
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        position: "absolute",
        bottom: 0,
        maxWidth: MAX_MODAL_WIDTH,
        width: MAX_MODAL_WIDTH,
        height: WELCOME_IMAGE_HEIGHT,

        [mediaQueries.smOrSmaller]: {
            backgroundSize: "contain",
            maxWidth: "100%",
            width: "100%",
            height: "100%",
            backgroundPositionY: "bottom",
            marginBlockEnd: spacing.medium_16,
        },
    },
});
